import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { isAudioSupported, debugAudioSystem } from '../utils/audioUtils';
import { audioFallback } from '../utils/audioFallback';
import { generateAudioUrlWithFallback, pregenerateAudio, refreshAllAudioCaches } from '../utils/audioGenerator';

import { getWordOfTheDayAudioUrl } from '../utils/audioUrlMapper';
import { API_BASE_URL } from '../config/api';
import { getWordOfTheDay, getPreviousWords, WordOfTheDay as WordOfTheDayType } from '../data/wordOfTheDayData';

interface Word {
  id: string;
  persian: string;
  english: string;
  transliteration: string;
  category: string;
  difficulty: 'beginner' | 'intermediate' | 'advanced';
  audioUrl?: string;
  example?: string;
  synonyms?: string[];
  antonyms?: string[];
  date: string;
}

const WordOfTheDay: React.FC = () => {
  const [currentWord, setCurrentWord] = useState<Word | null>(null);
  const [previousWords, setPreviousWords] = useState<Word[]>([]);
  const [loading, setLoading] = useState(true);
  const [showTransliteration, setShowTransliteration] = useState(false);
  const [showExample, setShowExample] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedWordForReview, setSelectedWordForReview] = useState<Word | null>(null);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [audioInitialized, setAudioInitialized] = useState(false);

  useEffect(() => {
    fetchWordOfTheDay();
  }, []);

  // Initialize audio system on first user interaction
  const initializeAudio = () => {
    if (!audioInitialized) {
      console.log('Initializing audio system...');
      debugAudioSystem();
      setAudioInitialized(true);
    }
  };

  // Pre-generate audio for current word and previous words
  const pregenerateWordAudio = async () => {
    if (!currentWord) return;
    
    const wordsToGenerate = [
      currentWord.persian,
      ...previousWords.map(word => word.persian)
    ];
    
    try {
      console.log('🔄 Pre-generating audio for word of the day...');
      await pregenerateAudio(wordsToGenerate);
    } catch (error) {
      console.warn('⚠️ Failed to pre-generate audio:', error);
    }
  };

  const fetchWordOfTheDay = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Get today's word from local data
      const todayWord = getWordOfTheDay();
      const previousWordsData = getPreviousWords(3);
      
      // Transform to match our interface
      const transformedWord: Word = {
        id: todayWord.id,
        persian: todayWord.persian,
        english: todayWord.english,
        transliteration: todayWord.transliteration,
        category: todayWord.category,
        difficulty: todayWord.difficulty,
        audioUrl: todayWord.audioUrl,
        example: todayWord.example,
        synonyms: todayWord.synonyms || [],
        antonyms: todayWord.antonyms || [],
        date: new Date().toISOString().split('T')[0]
      };
      
      setCurrentWord(transformedWord);
      
      // Transform previous words
      const transformedPreviousWords: Word[] = previousWordsData.map((word, index) => ({
        id: word.id,
        persian: word.persian,
        english: word.english,
        transliteration: word.transliteration,
        category: word.category,
        difficulty: word.difficulty,
        audioUrl: word.audioUrl,
        example: word.example,
        synonyms: word.synonyms || [],
        antonyms: word.antonyms || [],
        date: (() => {
          const date = new Date();
          date.setDate(date.getDate() - (index + 1));
          return date.toISOString().split('T')[0];
        })()
      }));
      
      setPreviousWords(transformedPreviousWords);
      
      // Pre-generate audio for all words
      setTimeout(() => {
        pregenerateWordAudio();
      }, 1000);
      
    } catch (error) {
      console.error('Error fetching word of the day:', error);
      setError('Failed to load word of the day. Please try again.');
      
      // Fallback to hardcoded data if local data fails
      setCurrentWord({
        id: '1',
        persian: 'سلام',
        english: 'Hello',
        transliteration: 'Salaam',
        category: 'Greetings',
        difficulty: 'beginner',
        example: 'سلام، چطوری؟',
        synonyms: ['درود', 'مرحبا'],
        antonyms: ['خداحافظ', 'بدرود'],
        date: new Date().toISOString().split('T')[0]
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePlayAudio = async () => {
    if (!currentWord) return;
    
    // Initialize audio system on first interaction
    initializeAudio();
    
    try {
      // Check if audio is supported
      if (!isAudioSupported()) {
        alert('Audio pronunciation is not supported in your browser. Please try a modern browser like Chrome, Firefox, or Safari.');
        return;
      }

      // Show loading state
      const button = document.querySelector('[data-audio-button]') as HTMLButtonElement;
      if (button) {
        button.disabled = true;
        button.innerHTML = '🔊 Playing...';
      }
      
      console.log('🎯 Attempting to play audio for word of the day:', currentWord.persian);
      
      // Use the direct audio URL from our data
      const audioUrl = currentWord.audioUrl || `/audio/word-of-the-day/${currentWord.english.toLowerCase()}.mp3`;
      console.log('🎵 Using word-of-the-day audio URL:', audioUrl);
      
      // Play the audio directly
      const audio = new Audio(audioUrl);
      await audio.play();
      console.log('✅ Audio played successfully');
      
    } catch (error) {
      console.error('❌ Error playing audio:', error);
      
      // If audio file failed, try TTS as fallback
      console.log('🔄 Audio file failed, no TTS fallback available');
      alert(`Audio not available for: ${currentWord.persian}`);
      
      // Provide specific error messages
      let errorMessage = 'Failed to play pronunciation. ';
      
      if (error instanceof Error) {
        if (error.message.includes('speech synthesis')) {
          errorMessage += 'Speech synthesis is not available. Please check your browser settings.';
        } else if (error.message.includes('voices')) {
          errorMessage += 'No Persian voices found. Please try a different browser.';
        } else {
          errorMessage += error.message;
        }
      }
      
      alert(errorMessage);
    } finally {
      // Reset button state
      const button = document.querySelector('[data-audio-button]') as HTMLButtonElement;
      if (button) {
        button.disabled = false;
        button.innerHTML = '🎵 Play Pronunciation';
      }
    }
  };

  const handleTestAudio = async () => {
    console.log('Test audio button clicked!'); // Debug log
    
    // Initialize audio system on first interaction
    initializeAudio();
    
    try {
      console.log('Testing audio functionality...');
      
      // Debug audio system first
      debugAudioSystem();
      
      // Check if audio is supported
      if (!isAudioSupported()) {
        alert('Audio pronunciation is not supported in your browser.');
        return;
      }

      // Test with a simple Persian word
      alert('سلام');
      console.log('Audio test successful');
      alert('Audio test successful! You should hear "سلام"');
      
    } catch (error) {
      console.error('Audio test failed:', error);
      alert(`Audio test failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const handleAddToLearningList = () => {
    // TODO: Implement add to learning list
    console.log('Adding to learning list:', currentWord?.persian);
  };

  const handleReviewWord = (word: Word) => {
    setSelectedWordForReview(word);
    setShowReviewModal(true);
  };

  const handleCloseReviewModal = () => {
    setShowReviewModal(false);
    setSelectedWordForReview(null);
  };

  const handleReviewWordAudio = async () => {
    if (!selectedWordForReview) return;
    
    // Initialize audio system on first interaction
    initializeAudio();
    
    try {
      // Check if audio is supported
      if (!isAudioSupported()) {
        alert('Audio pronunciation is not supported in your browser. Please try a modern browser like Chrome, Firefox, or Safari.');
        return;
      }

      // Show loading state
      const button = document.querySelector('[data-review-audio-button]') as HTMLButtonElement;
      if (button) {
        button.disabled = true;
        button.innerHTML = '<span>🔊</span><span>Playing...</span>';
      }
      
      console.log('Attempting to play audio for review word:', selectedWordForReview.persian);
      
      // Use the direct audio URL from our data
      const audioUrl = selectedWordForReview.audioUrl || `/audio/word-of-the-day/${selectedWordForReview.english.toLowerCase()}.mp3`;
      console.log('🎵 Using review word audio URL:', audioUrl);
      
      // Play the audio directly
      const audio = new Audio(audioUrl);
      await audio.play();
      console.log('Review word audio played successfully');
      
    } catch (error) {
      console.error('Error playing review word audio:', error);
      
      // If audio file failed, no TTS fallback available
      console.log('Audio file failed for review word, no TTS fallback available');
      alert(`Audio not available for: ${selectedWordForReview.persian}`);
      
      // Provide specific error messages
      let errorMessage = 'Failed to play pronunciation. ';
      
      if (error instanceof Error) {
        if (error.message.includes('speech synthesis')) {
          errorMessage += 'Speech synthesis is not available. Please check your browser settings.';
        } else if (error.message.includes('voices')) {
          errorMessage += 'No Persian voices found. Please try a different browser.';
        } else {
          errorMessage += error.message;
        }
      }
      
      alert(errorMessage);
    } finally {
      // Reset button state
      const button = document.querySelector('[data-review-audio-button]') as HTMLButtonElement;
      if (button) {
        button.disabled = false;
        button.innerHTML = '<span>🎵</span><span>Play Pronunciation</span>';
      }
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="text-center">
          <div className="text-red-500 text-xl mb-4">⚠️</div>
          <p className="text-gray-600 mb-4">{error}</p>
          <button 
            onClick={fetchWordOfTheDay}
            className="px-6 py-3 bg-emerald-500 text-white font-semibold rounded-lg hover:bg-emerald-600 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8" onClick={initializeAudio}>
      {/* Header */}
      <div className="text-center">
        <h1 className="text-4xl font-bold bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent mb-4">
          کلمه روز 📅
        </h1>
        <p className="text-lg text-white">
          Learn a new Persian word every day
        </p>
      </div>

      {/* Current Word */}
      {currentWord && (
        <div className="bg-gradient-to-br from-white to-emerald-50 rounded-3xl p-8 shadow-xl border border-emerald-100">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-4 rtl:space-x-reverse">
              <span className="px-4 py-2 bg-gradient-to-r from-emerald-500 to-teal-500 text-white rounded-full text-sm font-medium shadow-lg">
                {currentWord.difficulty === 'beginner' ? 'Beginner' :
                 currentWord.difficulty === 'intermediate' ? 'Intermediate' : 'Advanced'}
              </span>
              <span className="text-sm text-gray-500">
                {new Date(currentWord.date).toLocaleDateString('fa-IR')}
              </span>
            </div>
            <span className="px-4 py-2 bg-gradient-to-r from-blue-500 to-indigo-500 text-white rounded-full text-sm font-medium shadow-lg">
              {currentWord.category}
            </span>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Word Display */}
            <div className="text-center">
              <div className="text-7xl font-bold bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent mb-6">
                {currentWord.persian}
              </div>
              
              <div className="space-y-4">
                <div>
                  <button
                    onClick={() => setShowTransliteration(!showTransliteration)}
                    className="text-lg text-gray-600 hover:text-emerald-500 transition-colors duration-300"
                  >
                    {showTransliteration ? currentWord.transliteration : 'Show Pronunciation'}
                  </button>
                </div>
                
                <div className="text-3xl font-semibold text-gray-900">
                  {currentWord.english}
                </div>

                {currentWord.example && (
                  <div>
                    <button
                      onClick={() => setShowExample(!showExample)}
                      className="text-sm text-gray-500 hover:text-emerald-500 transition-colors duration-300"
                    >
                      {showExample ? 'Hide Example' : 'Show Example'}
                    </button>
                    {showExample && (
                      <div className="mt-3 p-4 bg-gradient-to-r from-emerald-50 to-teal-50 rounded-xl border border-emerald-200">
                        <p className="text-gray-700">{currentWord.example}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Actions */}
            <div className="space-y-4">
              <button
                onClick={handlePlayAudio}
                data-audio-button
                className="w-full py-4 px-6 bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-semibold rounded-xl hover:from-emerald-600 hover:to-teal-600 transition-all duration-300 flex items-center justify-center space-x-2 rtl:space-x-reverse shadow-lg hover:shadow-xl transform hover:scale-105"
              >
                <span>🎵</span>
                <span>Play Pronunciation</span>
              </button>

              <button
                onClick={handleTestAudio}
                data-test-audio-button
                className="w-full py-4 px-6 border-2 border-emerald-500 text-emerald-500 font-semibold rounded-xl hover:bg-emerald-50 transition-all duration-300 flex items-center justify-center space-x-2 rtl:space-x-reverse hover:border-emerald-600 cursor-pointer"
              >
                <span>🔊</span>
                <span>Test Audio</span>
              </button>

              <button
                onClick={async () => {
                  if (!currentWord) return;
                  
                  try {
                    console.log('🎤 Testing ganji-adabi audio generation for:', currentWord.persian);
                    
                    // Show loading state
                    const button = document.querySelector('[data-ganji-test-button]') as HTMLButtonElement;
                    if (button) {
                      button.disabled = true;
                      button.innerHTML = '🎤 Generating...';
                    }
                    
                    
                    const audioUrl = await generateAudioUrlWithFallback(currentWord.persian);
                    
                    if (audioUrl) {
                      console.log('✅ ganji-adabi audio generated successfully:', audioUrl);
                      
                      // Play the generated audio
                      const audio = new Audio(audioUrl);
                      await audio.play();
                      
                      alert(`✅ ganji-adabi audio generated and played successfully!\n\nWord: ${currentWord.persian}\nAudio URL: ${audioUrl}`);
                    } else {
                      console.log('⚠️ ganji-adabi generation failed, will use TTS fallback');
                      alert('⚠️ ganji-adabi generation failed, will use TTS fallback\n\nCheck console for details.');
                    }
                  } catch (error) {
                    console.error('❌ ganji-adabi test failed:', error);
                    alert(`❌ ganji-adabi test failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
                  } finally {
                    // Reset button state
                    const button = document.querySelector('[data-ganji-test-button]') as HTMLButtonElement;
                    if (button) {
                      button.disabled = false;
                      button.innerHTML = '🎤 Test ganji-adabi';
                    }
                  }
                }}
                data-ganji-test-button
                className="w-full py-4 px-6 border-2 border-purple-500 text-purple-500 font-semibold rounded-xl hover:bg-purple-50 transition-all duration-300 flex items-center justify-center space-x-2 rtl:space-x-reverse hover:border-purple-600 cursor-pointer"
              >
                <span>🎤</span>
                <span>Test ganji-adabi</span>
              </button>

              <button
                onClick={async () => {
                  try {
                    await refreshAllAudioCaches();
                    alert('✅ All audio caches refreshed successfully!\n\nCheck console for details.');
                  } catch (error) {
                    alert(`❌ Failed to refresh caches: ${error instanceof Error ? error.message : 'Unknown error'}`);
                  }
                }}
                className="w-full py-3 px-6 border-2 border-orange-500 text-orange-500 font-semibold rounded-xl hover:bg-orange-50 transition-all duration-300 flex items-center justify-center space-x-2 rtl:space-x-reverse hover:border-orange-600 cursor-pointer text-sm"
              >
                <span>🔄</span>
                <span>Refresh Cache</span>
              </button>

              <button
                onClick={() => {
                  alert('📊 Audio Cache Statistics:\n\nUsing browser TTS only - no cache available');
                }}
                className="w-full py-3 px-6 border-2 border-purple-500 text-purple-500 font-semibold rounded-xl hover:bg-purple-50 transition-all duration-300 flex items-center justify-center space-x-2 rtl:space-x-reverse hover:border-purple-600 cursor-pointer text-sm"
              >
                <span>📊</span>
                <span>Cache Stats</span>
              </button>

              <button
                onClick={handleAddToLearningList}
                className="w-full py-4 px-6 border-2 border-emerald-500 text-emerald-500 font-semibold rounded-xl hover:bg-emerald-50 transition-all duration-300 flex items-center justify-center space-x-2 rtl:space-x-reverse hover:border-emerald-600"
              >
                <span>📚</span>
                <span>Add to Learning List</span>
              </button>

              {/* Synonyms and Antonyms */}
              {currentWord.synonyms && currentWord.synonyms.length > 0 && (
                <div className="p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl border border-green-200">
                  <h4 className="font-semibold text-green-800 mb-2">Synonyms:</h4>
                  <div className="flex flex-wrap gap-2">
                    {currentWord.synonyms.map((synonym, index) => (
                      <span
                        key={index}
                        className="px-3 py-1 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-lg text-sm font-medium shadow-md"
                      >
                        {synonym}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {currentWord.antonyms && currentWord.antonyms.length > 0 && (
                <div className="p-4 bg-gradient-to-r from-red-50 to-pink-50 rounded-xl border border-red-200">
                  <h4 className="font-semibold text-red-800 mb-2">Antonyms:</h4>
                  <div className="flex flex-wrap gap-2">
                    {currentWord.antonyms.map((antonym, index) => (
                      <span
                        key={index}
                        className="px-3 py-1 bg-gradient-to-r from-red-500 to-pink-500 text-white rounded-lg text-sm font-medium shadow-md"
                      >
                        {antonym}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Previous Words */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-6">
          Previous Words
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {previousWords.map((word) => (
            <div
              key={word.id}
              className="bg-gradient-to-br from-white to-gray-50 rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-200 hover:border-emerald-300"
            >
              <div className="flex items-center justify-between mb-4">
                <span className="px-3 py-1 bg-gradient-to-r from-gray-500 to-gray-600 text-white rounded-lg text-xs font-medium">
                  {word.category}
                </span>
                <span className="text-xs text-gray-500">
                  {new Date(word.date).toLocaleDateString('fa-IR')}
                </span>
              </div>
              
              <div className="text-center">
                <div className="text-xl font-semibold mb-2 text-gray-900">
                  {word.persian}
                </div>
                <div className="text-sm mb-1 text-gray-600">
                  {word.transliteration}
                </div>
                <div className="text-lg font-semibold text-gray-900">
                  {word.english}
                </div>
              </div>

              <div className="mt-4 flex justify-center">
                <button 
                  onClick={() => handleReviewWord(word)}
                  className="px-4 py-2 bg-gradient-to-r from-emerald-500 to-teal-500 text-white rounded-lg hover:from-emerald-600 hover:to-teal-600 transition-all duration-300 text-sm font-medium shadow-md"
                >
                  Review Word
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Study Tips */}
      <div className="bg-gradient-to-br from-blue-500 via-purple-500 to-indigo-600 rounded-3xl p-8 text-white shadow-xl">
        <div className="text-center">
          <h3 className="text-3xl font-bold mb-6 text-white">
            Learning Tips 📝
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-6">
            <div className="text-center">
              <div className="text-4xl mb-4">🔄</div>
              <h4 className="font-semibold mb-3 text-lg">Spaced Repetition</h4>
              <p className="text-blue-100 text-sm leading-relaxed">
                Review words at specific time intervals to help them stay in memory longer
              </p>
            </div>
            <div className="text-center">
              <div className="text-4xl mb-4">🎯</div>
              <h4 className="font-semibold mb-3 text-lg">Daily Practice</h4>
              <p className="text-blue-100 text-sm leading-relaxed">
                Study at least 15 minutes every day to maintain steady progress
              </p>
            </div>
            <div className="text-center">
              <div className="text-4xl mb-4">📚</div>
              <h4 className="font-semibold mb-3 text-lg">Use in Sentences</h4>
              <p className="text-blue-100 text-sm leading-relaxed">
                Use words in different sentences to learn them better
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <div className="flex justify-between">
        <Link
          to="/dashboard"
          className="px-6 py-3 bg-gradient-to-r from-gray-500 to-gray-600 text-white font-semibold rounded-xl hover:from-gray-600 hover:to-gray-700 transition-all duration-300 shadow-lg"
        >
          ← Back to Dashboard
        </Link>
        <Link
          to="/flashcards"
          className="px-6 py-3 bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-semibold rounded-xl hover:from-emerald-600 hover:to-teal-600 transition-all duration-300 shadow-lg"
        >
          Start Flashcards →
        </Link>
      </div>

      {/* Review Word Modal */}
      {showReviewModal && selectedWordForReview && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-2xl font-bold text-gray-900">Review Word</h3>
              <button
                onClick={handleCloseReviewModal}
                className="text-gray-500 hover:text-gray-700 text-2xl font-bold"
              >
                ×
              </button>
            </div>
            
            <div className="text-center mb-6">
              <div className="text-6xl font-bold bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent mb-4">
                {selectedWordForReview.persian}
              </div>
              <div className="text-2xl font-semibold text-gray-900 mb-2">
                {selectedWordForReview.english}
              </div>
              <div className="text-lg text-gray-600 mb-4">
                {selectedWordForReview.transliteration}
              </div>
              
              <div className="flex justify-center space-x-4 rtl:space-x-reverse mb-6">
                <span className="px-4 py-2 bg-gradient-to-r from-emerald-500 to-teal-500 text-white rounded-full text-sm font-medium">
                  {selectedWordForReview.category}
                </span>
                <span className="px-4 py-2 bg-gradient-to-r from-blue-500 to-indigo-500 text-white rounded-full text-sm font-medium">
                  {selectedWordForReview.difficulty === 'beginner' ? 'Beginner' :
                   selectedWordForReview.difficulty === 'intermediate' ? 'Intermediate' : 'Advanced'}
                </span>
              </div>
            </div>

            <div className="space-y-4">
              <button
                onClick={handleReviewWordAudio}
                data-review-audio-button
                className="w-full py-3 px-6 bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-semibold rounded-xl hover:from-emerald-600 hover:to-teal-600 transition-all duration-300 flex items-center justify-center space-x-2 rtl:space-x-reverse"
              >
                <span>🎵</span>
                <span>Play Pronunciation</span>
              </button>

              <button
                onClick={() => handleAddToLearningList()}
                className="w-full py-3 px-6 border-2 border-emerald-500 text-emerald-500 font-semibold rounded-xl hover:bg-emerald-50 transition-all duration-300 flex items-center justify-center space-x-2 rtl:space-x-reverse"
              >
                <span>📚</span>
                <span>Add to Learning List</span>
              </button>


            </div>

            <div className="mt-6 text-center">
              <button
                onClick={handleCloseReviewModal}
                className="px-6 py-3 bg-gradient-to-r from-gray-500 to-gray-600 text-white font-semibold rounded-xl hover:from-gray-600 hover:to-gray-700 transition-all duration-300"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WordOfTheDay; 